import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play } from "lucide-react";

/**
 * Full-screen "conversation paused" veil, rendered by both the template and
 * default branches. Named for what the buttons DO (resume / end conversation)
 * rather than the page's internal handler names, so the component doesn't
 * need to know about handleTogglePause/handleEndTextChat.
 */
export interface PauseOverlayProps {
  isPaused: boolean;
  onResume: () => void;
  onEndConversation: () => void;
}

export function PauseOverlay({ isPaused, onResume, onEndConversation }: PauseOverlayProps) {
  return (
    <AnimatePresence>
      {isPaused && (
        <motion.div
          key="pause-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="fixed inset-0 z-[2000] flex items-center justify-center px-4"
          aria-modal="true"
          role="dialog"
          aria-label="Conversation paused"
        >
          <div className="absolute inset-0 bg-white/75 backdrop-blur-xl" />
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative w-full max-w-sm rounded-3xl bg-white p-7 text-center"
            style={{ boxShadow: "0 25px 60px -15px rgba(15,23,42,0.25), 0 0 0 1px #E5EAF0" }}
          >
            <div
              className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full"
              style={{
                background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
                boxShadow: "0 12px 32px -10px rgba(78,156,255,0.55)",
              }}
            >
              <Pause size={28} strokeWidth={2.25} className="text-white" />
            </div>
            <h2 className="text-[20px] font-semibold text-[#222B45]">Conversation paused</h2>
            <p className="mt-2 text-[14px] leading-relaxed text-[#64748B]">
              Take your time — your Genie is waiting quietly. Resume when you're ready.
            </p>
            <button
              type="button"
              onClick={onResume}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-semibold text-white shadow-lg transition-all hover:scale-[1.02] hover:opacity-95"
              style={{
                background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                boxShadow: "0 10px 28px -10px rgba(78,156,255,0.55)",
              }}
            >
              <Play size={16} strokeWidth={2.5} />
              Resume conversation
            </button>
            <button
              type="button"
              onClick={onEndConversation}
              className="mt-3 inline-flex w-full items-center justify-center rounded-xl px-6 py-2.5 text-[13px] font-medium text-[#64748B] transition-colors hover:text-[#222B45]"
            >
              End conversation instead
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PauseOverlay;
